import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { FocusCards } from "../../utils/ui/focus-cards";

const IMG = "/assets/images/bapat-academy";

const NAV_ITEMS = [
  { id: "contact-lenses", label: "Contact Lenses", src: `${IMG}/contact-lenses/image1.jpg` },
  { id: "lens-coatings", label: "Lens Coatings", src: `${IMG}/lens-coatings/image4.jpg` },
  { id: "myopia-control", label: "Myopia Control", src: `${IMG}/myopia-control/image1.jpg` },
  { id: "photochromatic", label: "Photochromatic", src: `${IMG}/photochromatic/image1.jpg` },
  { id: "polarised", label: "Polarised", src: `${IMG}/polarised/image1.jpg` },
  { id: "refractive-error", label: "Refractive Error", src: `${IMG}/refractive-error/image1.jpg` },
  { id: "refractive-index", label: "Refractive Index", src: `${IMG}/refractive-index/image2.jpg` },
  { id: "tinted-lenses", label: "Tinted Lenses", src: `${IMG}/tinted-lenses/image1.jpg` },
];

const CARDS = NAV_ITEMS.map(({ id, label, src }) => ({ id, title: label, src }));

const PAGE_STYLES = `

  :root{
    --navy:#0e2436;
    --navy-2:#15364f;
    --paper:#ffffff;
    --paper-2:#f7f7f5;
    --ink:#1c2b32;
    --ink-soft:#4d5c63;
    --amber:#c98a2e;
    --amber-deep:#a86f1e;
    --teal:#2e6b62;
    --line:#e2dccb;
    --card:#ffffff;
    --radius:4px;
    --maxw:1180px;
  }
  *{box-sizing:border-box;}
  html{scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none;}
  html::-webkit-scrollbar{display:none;width:0;height:0;}
  body::-webkit-scrollbar{display:none;width:0;height:0;}
  body{scrollbar-width:none;-ms-overflow-style:none;}
  body{
    margin:0;
    font-family:"Georgia","Iowan Old Style","Palatino Linotype",serif;
    background:var(--paper);
    color:var(--ink);
    line-height:1.6;
  }
  h1,h2,h3,h4{
    font-family:"Iowan Old Style","Palatino Linotype",Georgia,serif;
    font-weight:600;
    color:var(--navy);
    margin:0 0 .4em;
  }
  p{margin:0 0 1em;}
  img{max-width:100%;height:auto;display:block;}
  .wrap{max-width:var(--maxw);margin:0 auto;padding:0 40px;}

  /* ---------- Top identity bar ---------- */
  .app-sticky-header{
    position:fixed;
    top:var(--header-height, 160px);
    left:0;
    right:0;
    z-index:998;
  }
  .brandbar{
    background:var(--navy);
    color:#f2ece0;
    padding:14px 24px;
    display:flex;
    align-items:center;
    justify-content:center;
    font-family:Georgia,serif;
  }
  .brandbar .mark{
    display:flex;align-items:center;gap:10px;
    letter-spacing:.14em;
    font-size:.82rem;
    text-transform:uppercase;
  }
  .brandbar .mark .dot{
    width:9px;height:9px;border-radius:50%;
    background:var(--amber);
    display:inline-block;
  }
  .brandbar .tag{
    font-size:.72rem;
    color:#b9c3cb;
    letter-spacing:.04em;
    font-style:italic;
  }

  /* ---------- Nav bar (same style as EyeQ) ---------- */
  nav.sectionnav{
    position:relative;
    background:linear-gradient(180deg,#17405d 0%,#0f2c42 100%);
    border-bottom:3px solid var(--amber);
    box-shadow:0 4px 14px rgba(0,0,0,.22);
  }
  nav.sectionnav ul{
    list-style:none;
    margin:0;
    padding:0 14px;
    display:flex;
    align-items:stretch;
    gap:4px;
    width:100%;
    overflow-x:auto;
    scroll-behavior:smooth;
    -webkit-overflow-scrolling:touch;
    scrollbar-width:none;
    -ms-overflow-style:none;
  }
  nav.sectionnav ul::-webkit-scrollbar{display:none;height:0;width:0;}

  /* items keep their natural width; centred when everything fits */
  nav.sectionnav li{flex:0 0 auto;}
  nav.sectionnav li:first-child{margin-left:auto;}
  nav.sectionnav li:last-child{margin-right:auto;}

  nav.sectionnav ul button{
    position:relative;
    display:block;
    padding:16px 20px;
    color:#b9c9d6;
    background:transparent;
    border:none;
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.76rem;
    font-weight:500;
    letter-spacing:.1em;
    text-transform:uppercase;
    white-space:nowrap;
    cursor:pointer;
    transition:color .2s ease, background .2s ease;
  }

  /* animated underline */
  nav.sectionnav ul button::after{
    content:"";
    position:absolute;
    left:20px;
    right:20px;
    bottom:0;
    height:3px;
    border-radius:3px 3px 0 0;
    background:#e2a64a;
    transform:scaleX(0);
    transform-origin:center;
    transition:transform .25s ease;
  }
  nav.sectionnav ul button:hover{color:#fff;background:rgba(255,255,255,.06);}
  nav.sectionnav ul button:hover::after{transform:scaleX(.5);}
  nav.sectionnav ul button.active{
    color:#fff;
    font-weight:600;
    background:rgba(255,255,255,.08);
  }
  nav.sectionnav ul button.active::after{transform:scaleX(1);}
  nav.sectionnav ul button:focus-visible{outline:2px solid #e2a64a;outline-offset:-3px;}

  /* scroller arrows + edge fades (shown only when there is more to scroll) */
  nav.sectionnav .nav-scroll{
    position:absolute;
    top:0;
    bottom:0;
    z-index:2;
    width:64px;
    display:flex;
    align-items:center;
    border:0;
    padding:0 10px;
    cursor:pointer;
    opacity:0;
    visibility:hidden;
    pointer-events:none;
    transition:opacity .2s ease, visibility .2s ease;
  }
  nav.sectionnav .nav-scroll.left{
    left:0;
    justify-content:flex-start;
    background:linear-gradient(90deg,#0f2c42 35%,rgba(15,44,66,0));
  }
  nav.sectionnav .nav-scroll.right{
    right:0;
    justify-content:flex-end;
    background:linear-gradient(270deg,#0f2c42 35%,rgba(15,44,66,0));
  }
  nav.sectionnav .nav-scroll.show{
    opacity:1;
    visibility:visible;
    pointer-events:auto;
  }
  nav.sectionnav .nav-scroll svg{
    width:28px;
    height:28px;
    padding:5px;
    border-radius:50%;
    background:rgba(255,255,255,.14);
    stroke:#fff;
    fill:none;
    stroke-width:2.6;
    stroke-linecap:round;
    stroke-linejoin:round;
    transition:background .2s ease, transform .2s ease;
  }
  nav.sectionnav .nav-scroll:hover svg{background:var(--amber);transform:scale(1.1);}

  @media (max-width:768px){
    nav.sectionnav ul button{padding:14px 14px;font-size:.72rem;}
    nav.sectionnav ul button::after{left:14px;right:14px;}
    nav.sectionnav .nav-scroll{width:48px;}
  }

  /* ---------- Side prev/next arrows ---------- */
  .side-nav-arrow{
    position:fixed;
    top:50%;
    transform:translateY(-50%);
    width:50px;
    height:50px;
    border-radius:50%;
    border:none;
    background:#1c5fa8;
    color:#ffffff;
    display:flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
    z-index:997;
    box-shadow:0 6px 18px rgba(14,36,54,.35);
    transition:background .15s ease, transform .15s ease;
  }
  .side-nav-arrow:hover{
    background:#164a85;
    transform:translateY(-50%) scale(1.07);
  }
  .side-nav-arrow:active{
    transform:translateY(-50%) scale(.96);
  }
  .side-nav-arrow svg{
    width:22px;
    height:22px;
    stroke:#ffffff;
    fill:none;
    stroke-width:2.4;
    stroke-linecap:round;
    stroke-linejoin:round;
  }
  .side-nav-arrow.prev{left:18px;}
  .side-nav-arrow.next{right:18px;}
  @media (max-width:768px){
    .side-nav-arrow{width:40px;height:40px;}
    .side-nav-arrow svg{width:18px;height:18px;}
    .side-nav-arrow.prev{left:8px;}
    .side-nav-arrow.next{right:8px;}
  }

  /* ---------- Custom styled tooltips for prev/next arrows ---------- */
  .side-nav-arrow[data-tooltip]::after,
  .side-nav-arrow[data-tooltip]::before{
    opacity:0;
    visibility:hidden;
    position:absolute;
    top:50%;
    pointer-events:none;
    transition:opacity .18s ease, transform .18s ease;
    z-index:1000;
  }
  .side-nav-arrow[data-tooltip]::after{
    content:attr(data-tooltip);
    background:var(--navy);
    color:#f2ece0;
    border:1px solid var(--amber);
    padding:7px 14px;
    border-radius:4px;
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.76rem;
    letter-spacing:.04em;
    white-space:nowrap;
    box-shadow:0 8px 20px rgba(14,36,54,.4);
  }
  .side-nav-arrow[data-tooltip]::before{
    content:"";
    border:6px solid transparent;
  }
  .side-nav-arrow.prev[data-tooltip]::after{
    left:calc(100% + 14px);
    transform:translateY(-50%) translateX(-8px);
  }
  .side-nav-arrow.prev[data-tooltip]::before{
    left:calc(100% + 2px);
    transform:translateY(-50%);
    border-right-color:var(--amber);
  }
  .side-nav-arrow.next[data-tooltip]::after{
    right:calc(100% + 14px);
    transform:translateY(-50%) translateX(8px);
  }
  .side-nav-arrow.next[data-tooltip]::before{
    right:calc(100% + 2px);
    transform:translateY(-50%);
    border-left-color:var(--amber);
  }
  .side-nav-arrow[data-tooltip]:hover::after,
  .side-nav-arrow[data-tooltip]:hover::before{
    opacity:1;
    visibility:visible;
  }
  .side-nav-arrow.prev[data-tooltip]:hover::after{transform:translateY(-50%) translateX(0);}
  .side-nav-arrow.next[data-tooltip]:hover::after{transform:translateY(-50%) translateX(0);}
  @media (max-width:768px){
    .side-nav-arrow[data-tooltip]::after,
    .side-nav-arrow[data-tooltip]::before{display:none;}
  }

  /* ---------- Hero ---------- */
  .hero{
    background:
      radial-gradient(circle at 85% -10%, rgba(201,138,46,.16), transparent 55%),
      var(--paper);
    padding:64px 24px 48px;
    border-bottom:1px solid var(--line);
  }
  .hero .wrap{max-width:920px;}
  .hero .wrap.wide{max-width:var(--maxw);}
  .hero .eyebrow{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.76rem;
    letter-spacing:.16em;
    text-transform:uppercase;
    color:var(--amber-deep);
    margin-bottom:14px;
  }
  .hero h1{
    font-size:2.5rem;
    line-height:1.15;
    max-width:14ch;
  }
  .hero .wrap.wide h1{max-width:none;}
  .hero p.lede{
    font-size:1.08rem;
    color:var(--ink-soft);
    max-width:56ch;
    margin-top:14px;
    margin-bottom:36px;
  }

  /* ---------- Sections ---------- */
  section.doc{
    scroll-margin-top:58px;
    padding:56px 24px 24px;
    border-bottom:1px solid var(--line);
  }
  section.doc:nth-of-type(even){
    background:var(--paper-2);
  }
  .doc-head{margin-bottom:8px;}
  .doc-kicker{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.74rem;
    letter-spacing:.14em;
    text-transform:uppercase;
    color:var(--teal);
    margin-bottom:10px;
  }
  .doc-title{font-size:1.9rem;margin-bottom:6px;}
  .doc-tagline{
    font-style:italic;
    color:var(--ink-soft);
    font-size:1.02rem;
    margin-bottom:34px;
    max-width:58ch;
  }

  .block{margin:0 0 34px;}
  .block h3{font-size:1.28rem;margin-bottom:.3em;}
  .block h4{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.72rem;
    letter-spacing:.12em;
    text-transform:uppercase;
    color:var(--amber-deep);
    margin-bottom:6px;
  }
  .block .lead{color:var(--ink-soft);}

  figure{margin:18px 0;}
  figure img{
    border:1px solid var(--line);
    border-radius:var(--radius);
    background:#fff;
  }
  figure figcaption{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.78rem;
    color:var(--ink-soft);
    margin-top:8px;
  }
  .img-row{display:flex;gap:16px;flex-wrap:wrap;}
  .img-row figure{flex:1 1 260px;margin:18px 0;}

  /* card grids */
  .cards{
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(200px,1fr));
    gap:16px;
    margin:18px 0;
  }
  .card{
    background:var(--card);
    border:1px solid var(--line);
    border-left:3px solid var(--teal);
    border-radius:var(--radius);
    padding:16px 18px;
  }
  .card img{width:44px;height:44px;margin-bottom:10px;object-fit:contain;}
  .card h5{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.9rem;
    letter-spacing:.03em;
    color:var(--navy);
    margin:0 0 6px;
    text-transform:uppercase;
  }
  .card p{font-size:.94rem;color:var(--ink-soft);margin:0;}

  .stat{
    background:var(--card);
    border:1px solid var(--line);
    border-radius:var(--radius);
    padding:20px 18px;
    text-align:left;
  }
  .stat .num{
    font-size:1.9rem;
    color:var(--amber-deep);
    font-weight:600;
    display:block;
    margin-bottom:2px;
  }
  .stat .label{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.78rem;
    letter-spacing:.04em;
    text-transform:uppercase;
    color:var(--navy);
    display:block;
    margin-bottom:6px;
  }
  .stat p{font-size:.9rem;color:var(--ink-soft);margin:0;}

  /* compare table */
  table.compare{
    width:100%;
    border-collapse:collapse;
    background:var(--card);
    border:1px solid var(--line);
    margin:18px 0;
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.92rem;
  }
  table.compare th{
    background:var(--navy);
    color:#f2ece0;
    text-align:left;
    padding:10px 14px;
    font-weight:600;
    letter-spacing:.02em;
    font-size:.82rem;
  }
  table.compare td{
    padding:10px 14px;
    border-top:1px solid var(--line);
    vertical-align:top;
    color:var(--ink);
  }
  table.compare tr:nth-child(even) td{background:rgba(0,0,0,.03);}
  table.compare td.good{color:var(--teal);}

  ul.tick,ol.numbered{
    padding-left:1.1em;
    margin:0 0 1em;
  }
  ul.tick{list-style:none;padding-left:0;}
  ul.tick li{
    position:relative;
    padding-left:1.5em;
    margin-bottom:.5em;
  }
  ul.tick li::before{
    content:"✓";
    position:absolute;left:0;top:0;
    color:var(--teal);
    font-weight:700;
  }
  ol.numbered li{margin-bottom:.5em;}

  .pull{
    border-left:3px solid var(--amber);
    background:var(--card);
    padding:14px 18px;
    font-size:.95rem;
    color:var(--ink-soft);
    margin:18px 0;
  }
  .pull b{color:var(--ink);}

  .source-note{
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.76rem;
    color:var(--ink-soft);
    font-style:italic;
    margin-top:10px;
    padding-top:10px;
    border-top:1px dashed var(--line);
  }

  .cta{
    background:var(--navy);
    color:#f2ece0;
    padding:22px 24px;
    border-radius:var(--radius);
    margin-top:36px;
  }
  .cta h4{color:var(--amber);font-family:Georgia,serif;font-size:1.1rem;letter-spacing:0;text-transform:none;margin-bottom:4px;}
  .cta p{color:#cdd8e0;margin:0;}
  .cta .brand{
    margin-top:12px;
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.72rem;
    letter-spacing:.18em;
    text-transform:uppercase;
    color:var(--amber);
  }

  .two-col{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:16px;
  }
  @media (max-width:600px){
    .two-col{grid-template-columns:1fr;}
    .hero h1{font-size:1.9rem;}
    .doc-title{font-size:1.55rem;}
  }

  footer{
    background:var(--navy);
    color:#8fa0ac;
    text-align:center;
    padding:28px 24px;
    font-family:"Helvetica Neue",Arial,sans-serif;
    font-size:.78rem;
    letter-spacing:.04em;
  }
  footer .brand{color:var(--amber);letter-spacing:.18em;font-size:.74rem;}

  :root:not([data-theme="light"]){
    color-scheme: light;
  }
  @media (prefers-color-scheme: dark){
    :root:not([data-theme="light"]){
      --paper:#ffffff;
      --paper-2:#f7f7f5;
      --card:#ffffff;
      --ink:#1c2b32;
      --ink-soft:#4d5c63;
    }
  }
`;

export default function BapatAcademy() {
  // null = landing page with focus cards; otherwise the selected section id
  const [activeSection, setActiveSection] = useState(null);

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    measure();

    window.addEventListener("resize", measure);

    const raf = requestAnimationFrame(measure);

    return () => {
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
    };
  }, [activeSection]);

  const handleNavClick = (id) => {
    setActiveSection(id);
    const offset = headerRef.current?.offsetHeight || 0;
    window.scrollTo({ top: offset, behavior: "smooth" });
  };

  const currentIndex = NAV_ITEMS.findIndex((item) => item.id === activeSection);

  const goToStep = (step) => {
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + step + NAV_ITEMS.length) % NAV_ITEMS.length;
    handleNavClick(NAV_ITEMS[nextIndex].id);
  };

  // ---- Nav scroller ----
  const navListRef = useRef(null);
  const [navEdge, setNavEdge] = useState({ left: false, right: false });

  // show arrows only when there is more to scroll in that direction
  useEffect(() => {
    const list = navListRef.current;
    if (!list) return undefined;

    const updateEdges = () => {
      const left = list.scrollLeft > 4;
      const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 4;
      setNavEdge((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };

    updateEdges();
    list.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      list.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, []);

  // keep the active button centred inside the scrollable nav
  useEffect(() => {
    const list = navListRef.current;
    if (!list) return;
    const btn = list.querySelector("button.active");
    if (!btn) return;
    const target = btn.offsetLeft - (list.clientWidth - btn.offsetWidth) / 2;
    list.scrollTo({ left: target, behavior: "smooth" });
  }, [activeSection]);

  const scrollNav = (direction) => {
    const list = navListRef.current;
    if (!list) return;
    list.scrollBy({ left: direction * list.clientWidth * 0.6, behavior: "smooth" });
  };

  const prevItem = NAV_ITEMS[(currentIndex - 1 + NAV_ITEMS.length) % NAV_ITEMS.length];
  const nextItem = NAV_ITEMS[(currentIndex + 1) % NAV_ITEMS.length];

  return (
    <>
      <style>{PAGE_STYLES}</style>


      <div className="app-sticky-header" ref={headerRef}>
        <nav className="sectionnav" id="sectionnav">
          <button
            type="button"
            className={`nav-scroll left${navEdge.left ? " show" : ""}`}
            aria-label="Scroll menu left"
            tabIndex={navEdge.left ? 0 : -1}
            onClick={() => scrollNav(-1)}
          >
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
          </button>

          <ul ref={navListRef}>
            {NAV_ITEMS.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={activeSection === item.id ? "active" : ""}
                  onClick={() => handleNavClick(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={`nav-scroll right${navEdge.right ? " show" : ""}`}
            aria-label="Scroll menu right"
            tabIndex={navEdge.right ? 0 : -1}
            onClick={() => scrollNav(1)}
          >
            <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </nav>
      </div>
      <div aria-hidden="true" style={{ height: headerHeight }} />

      {activeSection && (
        <>
          <button
            type="button"
            className="side-nav-arrow prev"
            aria-label={`Previous: ${prevItem.label}`}
            data-tooltip={`Previous: ${prevItem.label}`}
            onClick={() => goToStep(-1)}
          >
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            type="button"
            className="side-nav-arrow next"
            aria-label={`Next: ${nextItem.label}`}
            data-tooltip={`Next: ${nextItem.label}`}
            onClick={() => goToStep(1)}
          >
            <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}

      {/* ================================================================ */}
      {/* LANDING — FOCUS CARDS FOR ALL EIGHT SECTIONS */}
      {/* ================================================================ */}
      {activeSection === null && (
        <section className="hero">
          <div className="wrap wide">
            <div className="eyebrow">Bapat Academy</div>
            <h1>Understand your eyes &amp; eyewear</h1>
            <p className="lede">Pick a topic to explore our guides on lenses, coatings, and vision care.</p>
            <FocusCards cards={CARDS} onSelect={(card) => handleNavClick(card.id)} />
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 1. CONTACT LENSES */}
      {/* ================================================================ */}
      {activeSection === "contact-lenses" && (
        <section className="doc" id="contact-lenses">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 01</div>
              <h2 className="doc-title">Contact Lenses</h2>
              <p className="doc-tagline">Daily vs. monthly, hydrogel vs. silicone hydrogel — knowing the numbers before you choose.</p>
            </div>

            <div className="block">
              <h4>Comparison</h4>
              <h3>Daily vs. Monthly Contact Lenses</h3>
              <table className="compare">
                <thead>
                  <tr>
                    <th></th>
                    <th>Daily — Hydrogel</th>
                    <th>Daily — Silicone Hydrogel</th>
                    <th>Monthly — Hydrogel</th>
                    <th>Monthly — Silicone Hydrogel</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Water content</td><td>58% to 78%</td><td>33% to 54%</td><td>38% to 62%</td><td>24% to 46%</td></tr>
                  <tr><td>Dk/t (oxygen transmissibility)</td><td>20 to 35</td><td>80 to 150</td><td>20 to 40</td><td>110 to 160</td></tr>
                  <tr><td>Daily usage</td><td colSpan="2">1 day usage, 12–13 hrs</td><td colSpan="2">10–12 hrs</td></tr>
                  <tr><td>Lens pack</td><td colSpan="2">30 &amp; 90 lens pack</td><td colSpan="2">3 or 6 lens pack</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h3>Which contact lens is best for me?</h3>
              <div className="img-row">
                <figure><img src="/assets/images/bapat-academy/contact-lenses/image1.jpg" alt="Daily disposable contact lens guidance" /></figure>
                <figure><img src="/assets/images/bapat-academy/contact-lenses/image2.jpg" alt="Monthly contact lens guidance" /></figure>
              </div>
            </div>

            <div className="block">
              <h4>Product options</h4>
              <h3>Products You Can Watch For</h3>
              <table className="compare">
                <thead>
                  <tr><th>Daily disposable</th><th>Monthly</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <ol className="numbered">
                        <li>My Day (Cooper Vision)</li>
                        <li>Dailies Total</li>
                        <li>Acuvue Oasys</li>
                        <li>Bio True One Day</li>
                      </ol>
                    </td>
                    <td>
                      <ol className="numbered">
                        <li>B&amp;L Ultra</li>
                        <li>Aspire Pro Cooper</li>
                        <li>Acuvue Oasys</li>
                        <li>Air Optix Hydraglyde</li>
                      </ol>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 2. LENS COATINGS */}
      {/* ================================================================ */}
      {activeSection === "lens-coatings" && (
        <section className="doc" id="lens-coatings">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 02 · Bapat Optics</div>
              <h2 className="doc-title">Lens Coatings</h2>
              <p className="doc-tagline">See the difference. Feel the comfort.</p>
            </div>

            <div className="block">
              <h4>Getting started</h4>
              <h3>Why a Coating Matters</h3>
              <div className="pull"><b>Essential concept</b> — Think of a coating as a lens's raincoat, sunscreen, and armor, all at once. The glass or plastic material enables vision, but the coating protects, clears, and comforts your eyes throughout the day.</div>

              <div className="cards">
                <div className="card">
                  <img src="/assets/images/bapat-academy/lens-coatings/image1.png" alt="Protects icon" />
                  <h5>Protects</h5>
                  <p>Guards against scratches, dust, and daily wear and tear.</p>
                </div>
                <div className="card">
                  <img src="/assets/images/bapat-academy/lens-coatings/image2.png" alt="Sharpens icon" />
                  <h5>Sharpens</h5>
                  <p>Cuts harsh glare and reflections for clearer, crisper vision.</p>
                </div>
                <div className="card">
                  <img src="/assets/images/bapat-academy/lens-coatings/image3.png" alt="Shields icon" />
                  <h5>Shields</h5>
                  <p>Blocks harmful UV rays and intense digital screen light.</p>
                </div>
              </div>
            </div>

            <div className="block">
              <h4>Coating 01</h4>
              <h3>Anti-Reflective Coating</h3>
              <p>Reflections off the lens surface hide the eyes and scatter light — this coating allows light to pass directly through the lens instead of bouncing back into your vision or reflecting toward others.</p>
              <figure><img src="/assets/images/bapat-academy/lens-coatings/image4.jpg" alt="Anti-reflective coating effect" /></figure>
              <table className="compare">
                <thead><tr><th>Ordinary lens</th><th>Anti-reflective lens</th></tr></thead>
                <tbody>
                  <tr><td>Distracting surface glare &amp; double images</td><td className="good">✔ Makes lenses nearly invisible</td></tr>
                  <tr><td>Halos around headlights &amp; streetlights at night</td><td className="good">✔ Sharper vision on screens &amp; night driving</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Coating 02</h4>
              <h3>Digital Screen Protection Coating</h3>
              <div className="img-row">
                <figure><img src="/assets/images/bapat-academy/lens-coatings/image5.jpg" alt="Before blue light shield" /></figure>
                <figure><img src="/assets/images/bapat-academy/lens-coatings/image6.jpg" alt="After blue light shield" /></figure>
              </div>
              <table className="compare">
                <thead><tr><th>Before (without filter)</th><th>After (with blue light shield)</th></tr></thead>
                <tbody>
                  <tr><td>Screen fatigue, dry &amp; tired watery eyes</td><td className="good">✔ Comfortable, relaxed eyes — no strain</td></tr>
                  <tr><td>Headaches after hours of screen time</td><td className="good">✔ Fewer screen-related headaches &amp; tension</td></tr>
                  <tr><td>Blurry text after long screen sessions</td><td className="good">✔ Crisp, clear text reading all day long</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Coating 03</h4>
              <h3>Scratch-Resistant Coating</h3>
              <p>Daily handling, cleaning with clothing, and accidental drops can dull a lens surface faster than people expect. A tough hard-coat resin layer reinforces the lens structure.</p>
              <figure><img src="/assets/images/bapat-academy/lens-coatings/image7.jpg" alt="Scratch-resistant coating" /></figure>
              <div className="pull">
                <b>Key benefits — Scratch resistance</b>
                <ul className="tick" style={{marginTop: '10px'}}>
                  <li>Withstands daily wear &amp; tear from wiping and storage.</li>
                  <li>Keeps lenses crystal clear and looking new longer.</li>
                  <li>Great choice for kids, active users, and rimless frames.</li>
                </ul>
              </div>
            </div>

            <div className="block">
              <h4>Coating 04</h4>
              <h3>Water-Repellent (Hydrophobic) Coating</h3>
              <p>Rain, sweat, steam, and daily environmental moisture are a constant reality — a hydrophobic coating creates a super-smooth surface so moisture cannot adhere.</p>
              <figure><img src="/assets/images/bapat-academy/lens-coatings/image8.jpg" alt="Water-repellent hydrophobic coating" /></figure>
              <table className="compare">
                <thead><tr><th>Wet side (standard)</th><th>Repelled (hydrophobic)</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Water droplets flatten, bead up irregularly, and smear across vision.</td>
                    <td className="good">✔ Water droplets slide and glide right off<br />✔ Significantly easier and faster to wipe clean<br />✔ Excellent performance in rain, sweat &amp; humid climate</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Coating 05</h4>
              <h3>Oleophobic (Oil-Repellent) Coating</h3>
              <p>Fingertips, facial oils, skin contact, and makeup leave a greasy film that dulls optical transparency — an oleophobic coating prevents oils from binding to the lens surface.</p>
              <figure><img src="/assets/images/bapat-academy/lens-coatings/image9.jpg" alt="Oleophobic oil-repellent coating" /></figure>
              <table className="compare">
                <thead><tr><th>Uncoated lens</th><th>Oleophobic coating</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Oils spread into stubborn hazy smudges requiring specialized fluid to remove.</td>
                    <td className="good">✔ Fingerprints wipe off effortlessly in one single swipe<br />✔ No greasy smudges from facial oils or skin contact<br />✔ Stays clean and smudge-free much longer between wipes</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>All-in-one solution</h4>
              <h3>One Lens. Every Protection.</h3>
              <p>Modern premium lenses combine all five essential coating layers into a unified multi-layer coating system:</p>
              <div className="cards">
                <div className="card"><p>✔ Anti-Reflective</p></div>
                <div className="card"><p>✔ Screen Protection</p></div>
                <div className="card"><p>✔ Scratch-Resistant</p></div>
                <div className="card"><p>✔ Water-Repellent</p></div>
                <div className="card"><p>✔ Oleophobic</p></div>
              </div>
            </div>

            <div className="cta">
              <h4>Ask us which combination of coatings fits your lifestyle</h4>
              <p>We'll help you choose the ideal lens surface technology for maximum visual comfort.</p>
              <div className="brand">Bapat Optics</div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 3. MYOPIA CONTROL LENSES */}
      {/* ================================================================ */}
      {activeSection === "myopia-control" && (
        <section className="doc" id="myopia-control">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 03 · Bapat Optics</div>
              <h2 className="doc-title">Myopia Control Lenses</h2>
              <p className="doc-tagline">Why myopia is rising so fast — and how next-gen lenses slow it down.</p>
            </div>

            <div className="block">
              <h4>The bigger picture</h4>
              <h3>Myopia Is Growing Fast</h3>
              <p>What was once an occasional vision issue is rapidly becoming a global epidemic — especially in young children.</p>
              <div className="cards">
                <div className="stat"><span className="num">50%</span><span className="label">Global myopia by 2050</span><p>Half the world's population is projected to be myopic within 30 years.</p></div>
                <div className="stat"><span className="num">~5 Billion</span><span className="label">People affected</span><p>Massive rise in high myopia risking severe ocular complications.</p></div>
                <div className="stat"><span className="num">22% → 50%</span><span className="label">Global prevalence</span><p>Myopia rates have more than doubled since 2000.</p></div>
              </div>

              <div className="pull">
                <b>Key epidemiological drivers</b>
                <ul className="tick" style={{marginTop: '10px'}}>
                  <li>Increased screen time &amp; intense near work are accelerating progression.</li>
                  <li>Reduced outdoor play and lack of natural sunlight exposure are key environmental drivers.</li>
                  <li>Onset is happening at much younger ages, leading to higher final prescriptions if unmanaged.</li>
                </ul>
                <div className="source-note">Source: Holden et al., Ophthalmology (2016) — Global Prevalence of Myopia and High Myopia, 2000–2050.</div>
              </div>
            </div>

            <div className="block">
              <h4>Understanding the problem</h4>
              <h3>What Is Myopia Progression?</h3>
              <p>In growing children, myopia is not just static blurry vision — it is an ongoing physical elongation of the eyeball.</p>
              <figure><img src="/assets/images/bapat-academy/myopia-control/image1.jpg" alt="Myopia progression, eyeball elongation" /></figure>
              <table className="compare">
                <thead><tr><th>Emmetropic eye (normal vision)</th><th>Progressive myopic eye</th></tr></thead>
                <tbody>
                  <tr><td className="good">✔ Light focus: directly on the retina</td><td>✖ Light focus: in front of the retina</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Why regular glasses aren't enough</h4>
              <h3>Peripheral Hyperopic Defocus</h3>
              <p>Standard single-vision lenses correct central vision perfectly, but inadvertently send a growth-stimulating signal to the peripheral retina.</p>
              <figure><img src="/assets/images/bapat-academy/myopia-control/image2.jpg" alt="Peripheral hyperopic defocus diagram" /></figure>
              <table className="compare">
                <thead><tr><th>Traditional single-vision lenses</th><th>Myopia control lenses (optimal)</th></tr></thead>
                <tbody>
                  <tr>
                    <td><b>Peripheral hyperopic defocus</b> — central light focuses on the retina, but peripheral light rays focus behind the retina. This creates a biological feedback signal that encourages the eyeball to grow longer to catch up.</td>
                    <td><b>Peripheral myopic defocus</b> — central light focuses sharply on the retina, while peripheral light rays are focused in front of the retina. This acts as an optical "stop signal" for eye elongation.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>The solution</h4>
              <h3>Four Approaches to Create Myopic Defocus</h3>
              <p>To effectively slow myopia progression, peripheral myopic defocus can be delivered using four primary clinical modalities:</p>
              <div className="cards">
                <div className="card">
                  <img src="/assets/images/bapat-academy/myopia-control/image3.png" alt="Spectacle lenses icon" />
                  <h5>✔ Spectacle lenses</h5>
                  <p>Specialized lens designs (HALT, DIMS, CARE) built into everyday eyeglass frames. Non-invasive and ideal for young children.</p>
                </div>
                <div className="card">
                  <img src="/assets/images/bapat-academy/myopia-control/image4.png" alt="Orthokeratology icon" />
                  <h5>✔ Orthokeratology (Ortho-K)</h5>
                  <p>Custom rigid gas-permeable contact lenses worn overnight. Gently reshapes the cornea to provide clear daytime vision and peripheral defocus.</p>
                </div>
                <div className="card">
                  <img src="/assets/images/bapat-academy/myopia-control/image5.png" alt="Multifocal contact lenses icon" />
                  <h5>✔ Multifocal contact lenses</h5>
                  <p>Soft daily contact lenses featuring concentric distance and near/defocus zones to slow axial elongation.</p>
                </div>
                <div className="card">
                  <img src="/assets/images/bapat-academy/myopia-control/image6.png" alt="Low-dose atropine drops icon" />
                  <h5>✔ Low-dose atropine drops</h5>
                  <p>Nightly pharmaceutical eye drops (e.g. 0.01%–0.05%) that slow axial growth, often prescribed in combination with spectacle lenses.</p>
                </div>
              </div>
            </div>

            <div className="block">
              <h4>Myopia control lenses</h4>
              <h3>DIMS, H.A.L.T. &amp; C.A.R.E.</h3>
              <figure><img src="/assets/images/bapat-academy/myopia-control/image7.jpg" alt="DIMS, HALT and CARE lens technology comparison" /></figure>
              <p>Modern spectacle lenses utilize sophisticated micro-lens structures to deliver continuous myopic defocus while maintaining clear central vision.</p>
              <table className="compare">
                <thead><tr><th>Brand &amp; lens</th><th>Technology name</th><th>Optical structure &amp; design</th></tr></thead>
                <tbody>
                  <tr>
                    <td>MiYOSMART (Hoya)</td>
                    <td>DIMS Technology (Defocus Incorporated Multiple Segments)</td>
                    <td>9.4mm clear central vision zone for distance visual acuity; 33mm honeycomb treatment zone composed of hundreds of small defocus segment lenslets (+3.50D).</td>
                  </tr>
                  <tr>
                    <td>Stellest (Essilor)</td>
                    <td>H.A.L.T. Technology (Highly Aspherical Lenslet Target)</td>
                    <td>1,021 highly aspherical lenslets arranged in 11 concentric rings; creates a volume of signal in front of the retina to slow eye elongation effectively.</td>
                  </tr>
                  <tr>
                    <td>ZEISS MyoCare</td>
                    <td>C.A.R.E. Technology (Cylindrical Annular Refractive Elements)</td>
                    <td>Alternating defocus and correction zones in concentric ring patterns; specifically optimized for pediatric ocular anatomy and refractive power requirements.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="cta">
              <h4>Every child's eyes are different</h4>
              <p>Ask us which myopia control approach is the best fit for yours.</p>
              <div className="brand">Bapat Optics</div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 4. PHOTOCHROMATIC LENS */}
      {/* ================================================================ */}
      {activeSection === "photochromatic" && (
        <section className="doc" id="photochromatic">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 04</div>
              <h2 className="doc-title">Photochromatic Lens</h2>
              <p className="doc-tagline">Glasses that darken in sunlight and clear indoors — automatically.</p>
            </div>

            <div className="block">
              <p>Photochromic lenses are one of those things that sound almost too convenient: glasses that darken in sunlight and clear indoors, automatically, without needing to carry a second pair.</p>
              <figure><img src="/assets/images/bapat-academy/photochromatic/image1.jpg" alt="Photochromatic lens darkening in sunlight" /></figure>
            </div>

            <div className="block">
              <h3>How Photochromatic Lenses Work</h3>
              <p>Photochromic lenses contain molecules embedded in the lens material that change structure when exposed to ultraviolet (UV) light. When UV is present — such as when outdoors during the day — the molecules darken the lens. When UV is absent — indoors, or at night — the molecules return to their original structure and the lens clears.</p>
              <figure><img src="/assets/images/bapat-academy/photochromatic/image2.jpg" alt="Photochromatic lens UV reaction diagram" /></figure>
              <ul className="tick">
                <li>Time taken for the lens to get dark is about 2 minutes.</li>
                <li>The more the UV exposure, the darker the shade.</li>
              </ul>
            </div>

            <div className="block">
              <h3>Why Photochromatic Lenses Are the Best Option for You</h3>
              <ol className="numbered">
                <li>If you have a prescription and have both indoor and outdoor work, photochromatic lenses are the best option for you.</li>
                <li>Clear indoors and automatically darker outdoors — so you don't need to switch between spectacles and sunglasses.</li>
                <li>Photochromatic lenses give you UV protection from both UVA and UVB rays, and give you the best cooling effect in sunlight.</li>
              </ol>
              <div className="pull"><b>Best Vision Comfort + UV Protection + Cooling Effect = Photochromatic Lenses</b></div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 5. POLARISED LENSES */}
      {/* ================================================================ */}
      {activeSection === "polarised" && (
        <section className="doc" id="polarised">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 05</div>
              <h2 className="doc-title">Polarised Lenses</h2>
              <p className="doc-tagline">Say goodbye to glare.</p>
            </div>

            <div className="block">
              <p>Have you ever been driving on a sunny day and found the reflection from the road too bright? Or struggled to see clearly near water because of strong reflections? This uncomfortable brightness is called <b>glare</b>, and it can make your eyes feel tired and your vision less comfortable.</p>
              <figure><img src="/assets/images/bapat-academy/polarised/image1.jpg" alt="Glare from road and water" /></figure>
              <p>Polarised lenses are designed to reduce this disturbing glare, making them especially useful for people who spend time outdoors, drive regularly, travel, or enjoy activities in bright sunlight.</p>
            </div>

            <div className="block">
              <h3>How Do Polarised Lenses Work?</h3>
              <p>Think of polarised lenses like a filter for your eyes. When sunlight hits surfaces such as roads, water, cars, or glass, it can reflect strongly toward your eyes. This reflected light creates glare.</p>
              <p>A polarised lens contains a special filter that blocks much of this reflected glare while allowing useful light to reach your eyes. This helps make your surroundings appear clearer and more comfortable, particularly in bright outdoor conditions.</p>
              <figure><img src="/assets/images/bapat-academy/polarised/image2.jpg" alt="How polarised lenses filter glare" /></figure>
            </div>

            <div className="block">
              <h3>What Difference Can You Notice?</h3>
              <p>With less glare reaching your eyes, you may experience:</p>
              <ul className="tick">
                <li>More comfortable vision in bright sunlight</li>
                <li>Less distraction from reflections while driving</li>
                <li>Better visibility of details outdoors</li>
                <li>Improved contrast in many bright environments</li>
                <li>A more relaxed visual experience during outdoor activities</li>
              </ul>
            </div>

            <div className="block">
              <h3>Who Can Benefit From Polarised Lenses?</h3>
              <div className="cards">
                <div className="card"><h5>🚗 Drive frequently</h5><p>Reflections from roads and other vehicles can be distracting. Polarisation helps reduce this reflected glare.</p></div>
                <div className="card"><h5>🏖️ Spend time outdoors</h5><p>Whether you're travelling, sightseeing, walking, or enjoying a sunny day, polarised lenses can make bright surroundings more comfortable.</p></div>
                <div className="card"><h5>🎣 Enjoy activities near water</h5><p>Water can produce intense reflections. Polarised lenses are particularly useful for reducing this type of glare.</p></div>
                <div className="card"><h5>☀️ Are sensitive to bright light</h5><p>If strong sunlight makes outdoor vision uncomfortable, reducing reflected glare can provide a more relaxed viewing experience.</p></div>
              </div>
            </div>

            <div className="block">
              <h3>Polarised Is More Than Just a Dark Sunglass</h3>
              <p>A common misconception is that polarised lenses simply make sunglasses darker. That's not their main purpose. The key benefit is their ability to reduce reflected glare. This is why two sunglasses with a similar tint can provide very different levels of visual comfort.</p>
              <p>However, polarisation does not block all bright light, and it does not replace UV protection. When choosing sunglasses, look for appropriate UV protection along with polarisation.</p>
            </div>

            <div className="block">
              <h3>Can I Get Polarised Lenses With My Prescription?</h3>
              <p><b>Yes!</b> If you already wear spectacles, polarised lenses can be made with your prescription. This means you don't have to choose between clear, corrected vision and glare protection.</p>
              <p>We can help you select the appropriate lens based on your prescription, lifestyle, outdoor exposure, and visual requirements.</p>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 6. REFRACTIVE ERROR */}
      {/* ================================================================ */}
      {activeSection === "refractive-error" && (
        <section className="doc" id="refractive-error">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 06</div>
              <h2 className="doc-title">Refractive Error</h2>
              <p className="doc-tagline">Blurry vision? It could be more than just tired eyes.</p>
            </div>

            <div className="block">
              <p>Have you noticed that road signs look blurry, small text is difficult to read, or your eyes feel tired after using a screen?</p>
              <figure><img src="/assets/images/bapat-academy/refractive-error/image1.jpg" alt="Blurry vision symptoms" /></figure>
              <p>These could be signs of a <b>refractive error</b> — a common vision problem that affects how clearly you see the world around you.</p>
              <p>The three most common refractive errors are <b>Myopia (short-sightedness), Hypermetropia (long-sightedness), and Astigmatism</b>. The good news? With a proper eye examination and the right vision correction, clearer and more comfortable vision is often possible.</p>
            </div>

            <div className="block">
              <h3>👀 What Is a Refractive Error?</h3>
              <figure><img src="/assets/images/bapat-academy/refractive-error/image2.png" alt="How clear vision works vs refractive error" /></figure>
              <p>Let's understand the three most common types.</p>
            </div>

            <div className="block">
              <h4>1. Myopia (short-sightedness)</h4>
              <h3>Clear Near Vision, Blurry Distance Vision</h3>
              <p>Do you find yourself sitting closer to the television? Are road signs or classroom boards difficult to see clearly? You may have <b>myopia</b>, commonly known as short-sightedness.</p>
              <p className="lead">Common symptoms seen in myopia:</p>
              <figure><img src="/assets/images/bapat-academy/refractive-error/image3.jpg" alt="Common symptoms of myopia" /></figure>
              <p>Myopia can gradually increase, especially during childhood and teenage years. Regular eye examinations can help detect changes in vision early.</p>
            </div>

            <div className="block">
              <h4>2. Hypermetropia (long-sightedness)</h4>
              <h3>When Reading and Near Work Become Tiring</h3>
              <p>If reading small print, using your mobile phone, or working on a computer makes your eyes feel tired, you may have <b>hypermetropia</b>, also known as long-sightedness. Your eyes may need to work harder to focus, particularly during near activities.</p>
              <figure><img src="/assets/images/bapat-academy/refractive-error/image4.png" alt="Hypermetropia symptoms" /></figure>
            </div>

            <div className="block">
              <h4>3. Astigmatism</h4>
              <h3>When Vision Looks Blurry or Distorted</h3>
              <p>Do letters sometimes look unclear even when they are large? Do lights appear stretched or scattered at night? These may be signs of <b>astigmatism</b>. Astigmatism can affect both distance and near vision. Objects may appear blurred, stretched, shadowed, or slightly distorted.</p>
              <figure><img src="/assets/images/bapat-academy/refractive-error/image5.jpg" alt="Astigmatism symptoms" /></figure>
            </div>

            <div className="block">
              <h3>How Do You Know Which Refractive Error You Have?</h3>
              <p>You may notice:</p>
              <ul className="tick">
                <li><b>Distance is blurry</b> → it may be myopia.</li>
                <li><b>Near work causes strain</b> → it may be hypermetropia.</li>
                <li><b>Vision looks blurred or distorted</b> → it may be astigmatism.</li>
              </ul>
            </div>

            <div className="block">
              <h3>👓 See Clearly. Live Comfortably.</h3>
              <p>At <b>Bapat Optics</b>, we believe an eye examination is about more than simply finding a spectacle number. Understanding your vision, your daily activities, and your visual requirements can help you find the most suitable solution for your lifestyle.</p>
              <p>Whether you are experiencing blurry distance vision, difficulty with near work, headaches, eye strain, or distorted vision, an eye examination can help you understand what your eyes need.</p>
            </div>

            <div className="cta">
              <h4>📅 Book Your Eye Test at Bapat Optics</h4>
              <p>Don't wait for blurry vision to interfere with your daily life. Take the first step towards clearer and more comfortable vision. 👉 Book your comprehensive eye test at Bapat Optics today — because clear vision helps you experience more of what matters.</p>
              <div className="brand">Bapat Optics</div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 7. REFRACTIVE INDEX */}
      {/* ================================================================ */}
      {activeSection === "refractive-index" && (
        <section className="doc" id="refractive-index">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 07 · Bapat Optics</div>
              <h2 className="doc-title">Understanding Refractive Index</h2>
              <p className="doc-tagline">Why the number on a lens label changes its thickness, weight &amp; price.</p>
            </div>

            <div className="block">
              <h4>01 | The basics</h4>
              <h3>What Is Refractive Index?</h3>
              <div className="pull"><b>Core definition</b> — A numerical value that describes how much a transparent material slows down and bends light rays passing through it.</div>
              <figure><img src="/assets/images/bapat-academy/refractive-index/image1.png" alt="Ray refraction diagram" /></figure>
              <ul className="tick">
                <li><b>Ray refraction in air:</b> when light strikes a lens material with a higher refractive index, the light ray bends closer to the normal line (normal = perpendicular to the surface).</li>
                <li><b>Bending efficiency:</b> higher index = more bending per millimeter of material thickness.</li>
                <li><b>Optical power advantage:</b> because a higher index material bends light more efficiently, significantly less physical material is needed to achieve the required focal power.</li>
              </ul>
            </div>

            <div className="block">
              <h4>02 | Why it matters</h4>
              <h3>Thinner Lenses, Same Power</h3>
              <figure><img src="/assets/images/bapat-academy/refractive-index/image2.jpg" alt="Lens thickness comparison diagram" /></figure>
              <div className="cards">
                <div className="card"><h5>Higher index</h5><p>Higher light bending power</p></div>
                <div className="card"><h5>Reduced thickness</h5><p>Less physical mass required</p></div>
                <div className="card"><h5>Decreased weight</h5><p>Lightweight, all-day comfort</p></div>
              </div>
              <div className="pull">
                <b>Key takeaways</b>
                <ul className="tick" style={{marginTop: '10px'}}>
                  <li>Same prescription power delivers noticeably less physical lens material.</li>
                  <li>Higher index eliminates thick 'bottle-bottom' edge profile in high minus prescriptions.</li>
                  <li>Reduced weight dramatically improves fit stability and wearing comfort.</li>
                </ul>
              </div>
            </div>

            <div className="block">
              <h4>03 | A real-world example</h4>
              <h3>A -6.00 Prescription, Five Ways</h3>
              <p>Consider a stronger prescription of -6.00 Diopters fitted into the same frame — look at the dramatic difference lens material selection makes:</p>
              <figure><img src="/assets/images/bapat-academy/refractive-index/image3.jpg" alt="Lens thickness chart for -6.00 prescription across five materials" /></figure>
              <table className="compare">
                <thead><tr><th>Index</th><th>Material name</th><th>Relative thickness</th><th>Visual thickness profile</th></tr></thead>
                <tbody>
                  <tr><td>1.50</td><td>CR-39 Standard Plastic</td><td>Thickest</td><td>████████ (Base 100%)</td></tr>
                  <tr><td>1.59</td><td>Polycarbonate</td><td>Thick</td><td>██████░░ (~80%)</td></tr>
                  <tr><td>1.60</td><td>MR-8 Resin</td><td>Medium</td><td>█████░░░ (~70%)</td></tr>
                  <tr><td>1.67</td><td>High Index</td><td>Thin</td><td>████░░░░ (~60%)</td></tr>
                  <tr><td>1.74</td><td>Ultra High Index</td><td>Thinnest</td><td>███░░░░░ (~50%)</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>04 | Quick reference</h4>
              <h3>Five Materials, Side by Side</h3>
              <p>The complete spectacle lens lineup categorized by optical index, material standard, and primary application:</p>
              <table className="compare">
                <thead><tr><th>Index</th><th>Material</th><th>Best for / ideal application</th></tr></thead>
                <tbody>
                  <tr><td>1.50</td><td>CR-39</td><td>Standard everyday wear, mild prescriptions.</td></tr>
                  <tr><td>1.59</td><td>Polycarbonate</td><td>High impact-resistance, ideal for children, sports, &amp; safety eyewear.</td></tr>
                  <tr><td>1.60</td><td>MR-8</td><td>Lightweight, durable resin with excellent optical clarity &amp; strength for rimless frames.</td></tr>
                  <tr><td>1.67</td><td>High Index</td><td>Thin, sleek profile designed for moderate-to-high prescriptions.</td></tr>
                  <tr><td>1.74</td><td>Ultra High Index</td><td>Thinnest &amp; lightest option available; best for strong, high prescriptions.</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>05 | Selection guide</h4>
              <h3>Higher Index, Thinner Life</h3>
              <p>Selecting the ideal index depends directly on your prescription power, frame style, and desired aesthetic outcome:</p>
              <table className="compare">
                <thead><tr><th>Mild Rx — 0.00 to ±2.00 D</th><th>Moderate Rx — ±2.25 to ±4.00 D</th><th>Higher Rx — Above ±4.25 D</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Standard index (1.50) works great. Offers supreme clarity and economic value.</td>
                    <td>Mid-to-high index (1.59–1.60) balances thinness, weight, and durability.</td>
                    <td>Ultra-high index (1.67–1.74) keeps lenses remarkably slim and lightweight.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="cta">
              <h4>Not sure which index fits your prescription?</h4>
              <p>Ask us — we'll help you choose the perfect material and coating combination.</p>
              <div className="brand">Bapat Optics</div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================ */}
      {/* 8. TINTED LENSES GUIDE */}
      {/* ================================================================ */}
      {activeSection === "tinted-lenses" && (
        <section className="doc" id="tinted-lenses">
          <div className="wrap">
            <div className="doc-head">
              <div className="doc-kicker">Guide 08 · Bapat Optics</div>
              <h2 className="doc-title">Tinted Lenses Guide</h2>
              <p className="doc-tagline">How tint changes brightness, visual comfort, UV protection, and personal appearance.</p>
              <figure style={{maxWidth: '320px'}}><img src="/assets/images/bapat-academy/tinted-lenses/image1.jpg" alt="Tinted lenses overview" /></figure>
            </div>

            <div className="block">
              <h4>The fundamentals</h4>
              <h3>Same World. More Comfort.</h3>
              <p>Tinted lenses alter how visible light reaches your eyes, transforming harsh outdoor glare into soft, relaxing visual comfort.</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image2.jpg" alt="Without tint vs with tint comparison" /></figure>
              <table className="compare">
                <thead><tr><th>Without tint (clear)</th><th>With tint (customized)</th></tr></thead>
                <tbody>
                  <tr><td>✖ Bright and unwanted sunlight</td><td className="good">✔ Softer, relaxed vision</td></tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Key advantages</h4>
              <h3>Why Tinted Lenses Are Best For You</h3>
              <p>Tinted eyewear delivers a specialized combination of optical functionality, comfort, and personalized aesthetic style.</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image3.png" alt="Key advantages of tinted lenses" /></figure>
              <p className="lead" style={{marginTop: '8px'}}>Tint selection guide</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image4.jpg" alt="Different types of tints — grey, brown, green, yellow, blue, rose" /></figure>
            </div>

            <div className="block">
              <h4>Tint density spectrum</h4>
              <h3>Understanding Tint Percentages</h3>
              <p>The tint percentage indicates how much visible light the lens absorbs. Higher percentages correspond to darker lenses.</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image5.png" alt="Tint density spectrum from 0% to 85%" /></figure>
              <table className="compare">
                <thead><tr><th>0% Clear</th><th>15% Very light</th><th>25% Light</th><th>50% Medium</th><th>75% Dark</th><th>85% Very dark</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Pure clarity; no light reduction.</td>
                    <td>Subtle cosmetic accent; indoor comfort.</td>
                    <td>Mild glare reduction; stylish fashion tint.</td>
                    <td>Balanced tint not ideal for outside.</td>
                    <td>Standard sunglass dark; bright sun protection.</td>
                    <td>Maximum darkness; intense tropical glare/marine.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="block">
              <h4>Target audiences</h4>
              <h3>Who Can Benefit From Tinted Lenses?</h3>
              <p>Tinted lenses are highly beneficial across a wide range of professional fields and active lifestyles:</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image6.png" alt="Who can benefit from tinted lenses" /></figure>
            </div>

            <div className="block">
              <h4>Critical safety factor</h4>
              <h3>Tint vs. UV Protection</h3>
              <p>A common misconception is that darker lens tints automatically equal better UV protection. In truth, tint and UV blocking serve entirely different functions:</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image7.jpg" alt="Tint vs UV protection explained" /></figure>
            </div>

            <div className="block">
              <h4>Prescription integration</h4>
              <h3>Tint + Prescription Lens Options</h3>
              <p>You don't need to choose between sharp sight and sun comfort. Customized tints can be built directly into your exact optical prescription.</p>
              <figure><img src="/assets/images/bapat-academy/tinted-lenses/image8.png" alt="Tint combined with prescription lenses" /></figure>
            </div>

            <div className="cta">
              <h4>A Better Choice Starts With The Right Tint.</h4>
              <p>The right tint. The right comfort. The right choice for you.</p>
              <div className="brand">Bapat Optics</div>
            </div>
          </div>
        </section>
      )}

    </>
  );
}